<script setup lang="ts">
  import 'flag-icons/css/flag-icons.min.css';
  import { computed } from 'vue';

  import { SUPPORTED_LANGUAGES } from '@stuut/api/models';
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';

  // Shared BCP-47 language picker used by both:
  // - `RetellLanguageSelector` (per-partner `partner.retell_language_code`)
  // - `LiveKitPersonalityEditor` (call-agent `language_code`)
  // Drives `@stuut/api/models#SUPPORTED_LANGUAGES`, which is itself kept in sync with
  // stuut-py's `stuut.livekit.types.{Language, Locale}` literals.
  const props = withDefaults(
    defineProps<{
      // Placeholder shown when no language is selected (e.g. "Auto-detect").
      placeholder?: string;
      // Whether to show a "Clear" option that resolves to `null` on select.
      clearable?: boolean;
      // Label for the clear option (default: "Clear").
      clearLabel?: string;
      // Disabled state — useful when the parent form has the picker locked out.
      disabled?: boolean;
      // Optional CSS class for the trigger.
      triggerClass?: string;
      // Specific codes to show but disable (e.g. ['multi'] when Hardcoded mode is active).
      disabledCodes?: string[];
      // When true, hides the unset/clear option so users can't pick "no language" —
      // useful when the parent form requires a concrete value (e.g. Hardcoded STT mode).
      required?: boolean;
    }>(),
    {
      placeholder: '— Not set —',
      clearable: false,
      clearLabel: 'Clear',
      disabled: false,
      triggerClass: 'justify-between',
      disabledCodes: () => [],
      required: false,
    },
  );

  const languageCode = defineModel<string | null>({ required: true });

  const CLEAR_SENTINEL = '__clear__';
  const UNSET_SENTINEL = '__unset__';

  const flagClass = (code: string | null): string => {
    if (!code) return 'fi-xx';
    if (code === 'multi') return 'fi-un';
    if (code === 'es-419') return 'fi-mx';
    const region = code.split('-')[1]?.toLowerCase();
    return region ? `fi-${region}` : 'fi-xx';
  };

  const options = computed(() =>
    Object.entries(SUPPORTED_LANGUAGES).map(([code, label]) => ({ code, label })),
  );

  // Select's `model-value` can't be `null` directly — use a sentinel for the unset state
  // and another for the explicit "Clear" action.
  const selectModelValue = computed<string>(() => languageCode.value ?? UNSET_SENTINEL);

  const handleSelect = (val: string | null | undefined) => {
    if (!val) return;
    if (val === UNSET_SENTINEL || val === CLEAR_SENTINEL) {
      languageCode.value = null;
      return;
    }
    languageCode.value = val;
  };
</script>

<template>
  <Select :disabled :model-value="selectModelValue" @update:model-value="handleSelect">
    <SelectTrigger :class="triggerClass">
      <SelectValue>
        <template v-if="languageCode">
          <i class="fi mr-2" :class="flagClass(languageCode)" />
          <span>{{ SUPPORTED_LANGUAGES[languageCode] ?? languageCode }}</span>
        </template>
        <span v-else class="text-muted-foreground">{{ placeholder }}</span>
      </SelectValue>
    </SelectTrigger>
    <SelectContent>
      <SelectItem v-if="clearable && !required" :value="CLEAR_SENTINEL">
        <span class="text-muted-foreground">{{ clearLabel }}</span>
      </SelectItem>
      <SelectItem v-else-if="!required" :value="UNSET_SENTINEL">
        <span class="text-muted-foreground">{{ placeholder }}</span>
      </SelectItem>
      <SelectItem
        v-for="option in options"
        :key="option.code"
        :value="option.code"
        :disabled="props.disabledCodes.includes(option.code)"
      >
        <i class="fi mr-2" :class="flagClass(option.code)" />
        <span>{{ option.label }}</span>
      </SelectItem>
    </SelectContent>
  </Select>
</template>
